import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import moment from "moment";
import PropTypes from "prop-types";
import { validate as uuidValidate } from "uuid";


import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import CovertButton from "@cayuse/shared-react.components.covert-button";
import Icon from "@cayuse/shared-react.components.icon";
import { SetView } from "@cayuse/shared-react.components.set-view";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyArray, isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import FeedTable from "components/FeedTable";
import TextButton from "components/TextButton";
import ModificationModals from "containers/Modifications/Modals";
import {
	AWARDS_BASE_ROUTE,
	AWARD_REVIEW_STATUS,
	COLUMN_HEADER_KEYS,
	DEFAULT_MODIFICATION_COLUMN_HEADERS,
	DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS,
	MODIFICATION_COMPLETED,
	MODIFICATION_DRAFT,
	SUBAWARDS_BASE_ROUTE,
} from "globals/constants";
import { useActiveColumns, useComponentDidMount, useDictionary, usePagination } from "hooks";
import { useColumnHeaders } from "hooks/useColumnHeaders";
import {
	refreshFormModifications,
	resetModifications,
	setPaging,
	cancelModification,
	continueModification,
	completeModification,
	getFormModifications,
} from "store/actions/modifications";
import { columnsSelectors, formSelectors, modificationListSelectors, tenantSelectors } from "store/selectors";
import { getAward, getAwardValues } from "store/actions/award";
import { getSubawardValues } from "store/actions/subaward";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { loadActiveColumns } from "services/columnsService";
import { notifyError } from "../../utils";
import { TABLE_TYPES } from "globals/constants/tableTypes";

import styles from "./Modifications.css";


const Modifications = ({
	currentModification,
	formApi,
	formId,
	formType,
	formStatus,
	reviewStatus,
	isFormAdmin,
	canReroute,
	parentId,
	setCurrentModification,
	setIsModificationChange,
	availabletModificationsColumns,
	setShouldUpdateForm,
}) => {
	const isFormSaving = useSelector(formSelectors.savingIndicator);
	const { isEditMode } = useSelector(formSelectors.entity);
	const { awardId, modificationNumber, subawardId } = useParams();
	const history = useHistory();
	const { search } = useLocation();
	const isSubaward = uuidValidate(subawardId);

	// State shared with multiple components/functions
	const [ comment, setComment ] = useState("");
	const [ formattedRows, setFormattedRows ] = useState([]);
	const [ reason, setReason ] = useState(null);
	const [ showFeedTable, setShowFeedTable ] = useState(true);
	const [ updateModifications, setUpdateModifications ] = useState(false);
	const [ availableColumns, setAvailableColumns ]
		= useState(isSubaward ? DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS : DEFAULT_MODIFICATION_COLUMN_HEADERS);
	const { updateActiveColumns }
		= useActiveColumns(isSubaward ? TABLE_TYPES.SUBAWARD_MODIFICATION : TABLE_TYPES.AWARD_MODIFICATION);

	const {
		columnHeaders: defaultModificationsColumns,
	} = useColumnHeaders(isSubaward ? COLUMN_HEADER_KEYS.SUBAWARD_MODIFICATIONS : COLUMN_HEADER_KEYS.MODIFICATIONS, {
		defaultColumnHeaders: isSubaward ? DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS : DEFAULT_MODIFICATION_COLUMN_HEADERS
	});

	const {
		items,
		totalItems,
		latestModificationNumber = 0,
	} = useSelector(modificationListSelectors.content);
	const isRefresh = useSelector(modificationListSelectors.isRefresh);
	const isLoading = useSelector(modificationListSelectors.isFetching);
	const { activeColumns: storedActiveColumns } =
		useSelector(isSubaward ? columnsSelectors.subawardModifications : columnsSelectors.modifications);
	const { dateFormat } = useSelector(tenantSelectors.tenant);

	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = usePagination("modifications", setPaging);
	const componentDidMount = useComponentDidMount();
	const dictionary = useDictionary("modifications");

	const lowerCaseFormType = formType.toLowerCase();
	const capitalizedFormType = lowerCaseFormType.charAt(0).toUpperCase() + lowerCaseFormType.slice(1);
	const formSpecificDictionary = dictionary[lowerCaseFormType];
	const tab = new URLSearchParams(search).get("tab");

	// Modification Type Object
	const hasDraft = useMemo(() => (items ?? []).some(item => item.modificationStatus === MODIFICATION_DRAFT), [ items ]);
	const formInfo = {
		formId,
		formType,
		parentId,
		awardId,
		isSubaward
	};

	const isLatestModification = parseInt(currentModification.modificationNumber ?? 0) === latestModificationNumber;
	const hasModifications = isNonEmptyArray(items) && items.length > 1;

	const isDraftSelected = currentModification.modificationStatus === MODIFICATION_DRAFT;

	const isModificationUnderReview = !isSubaward && hasModifications && isLatestModification
		&& formStatus === AWARD_REVIEW_STATUS.inReview && reviewStatus === AWARD_REVIEW_STATUS.started;

	const validDataLoaded = useMemo(() =>
		(isSubaward && items.every(item => item.subawardId)) ||
		(!isSubaward && items.every(item => !item.subawardId)),
	[ isSubaward, items ]);

	const setCurrentModificationNumber = (selectedAwardId, selectedSubawardId, selectedModificationNumber) => {
		const searchParams = tab ? `?tab=${tab}` : "";
		if(isSubaward) {
			history.push(`/sp${SUBAWARDS_BASE_ROUTE}/${selectedSubawardId}/${selectedModificationNumber}${searchParams}`);
		} else {
			history.push(`/sp${AWARDS_BASE_ROUTE}/${selectedAwardId}/${selectedModificationNumber}${searchParams}`);
		}
	};

	const verifyDefaultColumnTypes = (storedColumns, defaultColumns) => {
		storedColumns.forEach(storedColumn => {
			const defaultColumn = defaultColumns.find(column => column.id === storedColumn.id);
			if (defaultColumn && !storedColumn.type) {
				storedColumn.type = defaultColumn.type;
			}
		});

		return storedColumns;
	};

	const activeColumns = useMemo(() =>
		isNonEmptyArray(storedActiveColumns) ?
			verifyDefaultColumnTypes(storedActiveColumns, defaultModificationsColumns) :
			defaultModificationsColumns,
		[ storedActiveColumns, defaultModificationsColumns ]);

	useEffect(() => {
		if (!storedActiveColumns || storedActiveColumns.length === 0) {
			const type = isSubaward ? "SUBAWARD_MODIFICATION" : "AWARD_MODIFICATION";
			loadActiveColumns(type, activeColumns)().catch(error => {
				notifyError("Error loading columns:", error);
			});
		}
	}, [ ]);

	useEffect(()=> {
		const newColumnHeaders = [ ...defaultModificationsColumns,  ...availabletModificationsColumns ];
		setAvailableColumns(newColumnHeaders);
	},[ availabletModificationsColumns, defaultModificationsColumns ]);


	/* Start - Comment Modal */
	const [ commentVisible, setCommentVisible ] = useState(false);
	const [ commentTitle, setCommentTitle ] = useState("");

	const onCommentOpen = (createdBy, comment) => {
		setCommentTitle("Modification Comment from " + createdBy);
		setComment(comment);
		setCommentVisible(true);
	};

	const onCommentClose = () => {
		setCommentVisible(false);
	};

	const commentModalData = {
		comment,
		commentTitle,
		commentVisible,
		onCommentClose
	};
	/* END - Comment Modal */

	/* Start - Delete Modal */
	const [ confirmDeleteVisible, setConfirmDeleteVisible ] = useState(false);

	const onConfirmDeleteOpen = () => {
		setConfirmDeleteVisible(true);
	};

	const onConfirmDelete = async () => {
		await cancelModification(formInfo, formApi);
		setCurrentModification({});
		setConfirmDeleteVisible(false);
	};

	const onConfirmDeleteClose = () => {
		setConfirmDeleteVisible(false);
	};

	const confirmDeleteModalData = {
		confirmDeleteVisible,
		onConfirmDelete,
		onConfirmDeleteClose,
	};
	/* END - Delete Modal */

	/* Start - Modify Form Modal */
	const [ modifyFormModalVisible, setModifyFormModalVisible ] = useState(false);

	const onModifyFormOpen = () => {
		setReason(null);
		setModifyFormModalVisible(true);
	};

	const onModifyFormClose = () => {
		setReason(null);
		setModifyFormModalVisible(false);
	};

	const onModifyFormContinue = async () => {
		await continueModification(formInfo, formApi, reason);
		setCurrentModification({});
		setModifyFormModalVisible(false);
		setReason(null);
		setCurrentModificationNumber(awardId, subawardId, latestModificationNumber + 1);
	};

	const modifyFormModalData = {
		formType: capitalizedFormType,
		isLoading,
		modifyFormModalVisible,
		onModifyFormClose,
		onModifyFormContinue,
		reason,
		setReason
	};
	/* END - Modify Form Modal */

	/* Start - Activate Draft Modal */
	const [ activateDraftModalVisible, setActivateDraftModalVisible ] = useState(false);

	const onActivateDraftOpen = useCallback(() => {
		setReason(currentModification.category);
		setComment("");
		setActivateDraftModalVisible(true);
	}, [ currentModification.category ]);

	const onActivateDraftClose = () => {
		setReason(null);
		setActivateDraftModalVisible(false);
	};

	const onCompleteModification = async reroute => {
		await completeModification(formInfo, formApi, reason, comment, reroute);
		setCurrentModification({});
		setActivateDraftModalVisible(false);
		if (isSubaward) {
			getSubawardValues({ awardId, subawardId })
		} else {
			if (reroute) {
				await getAward(formId, { isRefresh: true });
				setShouldUpdateForm(true);
			}
			await getAwardValues({ awardId: formId });
		}
	};

	const activateDraftModalData = {
		activateDraftModalVisible,
		isLoading,
		formType: capitalizedFormType,
		onActivateDraftOpen,
		onActivateDraftClose,
		onCompleteModification,
		reason,
		comment,
		setComment,
		setReason
	};
	/* END - Activate Draft Modal */

	const formatDate = date =>{
		if(moment(date).isValid()) {
			return moment(date).format(dateFormat);
		}else{
			return date;
		}
	};

	useEffect(() => {
		if(items.length === 0 || !validDataLoaded) {
			return;
		}

		let selectedModificationNumber = !isNaN(modificationNumber) ? Number(modificationNumber) : NaN;
		const largestModificationNumber = isNonEmptyArray(items) ? Math.max(...items.map(m => m.modificationNumber)) : selectedModificationNumber;
		selectedModificationNumber = !isNaN(selectedModificationNumber) && selectedModificationNumber <= largestModificationNumber
			? selectedModificationNumber : largestModificationNumber;

		const newRows =  (items ?? []).map(modification => {
			modification.category = modification.modificationType;

			if(modification.modificationStatus === MODIFICATION_DRAFT) {
				modification.modificationDate = "draft modification";
			} else {
				modification.modificationDate = formatDate(modification.updateDate);
			}

			if(modification?.modificationType?.startsWith("Original")) {
				modification.modifiedByName = ""; // If original form modified by should be blank.
			} else {
				modification.modifiedByName = modification.updateUserName;
			}
			if(isNonEmptyString(modification.modificationComment)) {
				const commentText = modification.modificationComment;
				modification.comment = (
					<TextButton appearAsLink onClick={() => onCommentOpen(modification.modifiedByName, commentText)}>
						{dictionary.viewComment || "view"}
					</TextButton>
				);
			}
			const columnValues = { ...modification.columnValues };
			const newModification = { ...modification, ...columnValues };
			delete newModification.columnValues;

			const propertyName = isSubaward ? "subawardNumber" : "awardNumber";
			if(!isNonEmptyObject(currentModification) && selectedModificationNumber === modification.modificationNumber) {
				newModification._isSelected = true;
				setCurrentModification(newModification);
				setCurrentModificationNumber(awardId, subawardId, selectedModificationNumber);
			} else if(isNonEmptyObject(currentModification) &&
				currentModification[propertyName] === newModification[propertyName]
			) {
				newModification._isSelected = true;
				setCurrentModification(newModification);
			} else{
				newModification._isSelected = false;
			}

			newModification.formNumber = modification.columnValues[`${lowerCaseFormType}Number`];

			return newModification;
		});
		setFormattedRows(newRows);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ items ]); //disable eslint to avoid running useMemo multiple times

	const onRowClick = clickedRow => {
		if(isEditMode) {
			return;
		}

		const clickedRowId = clickedRow[`${lowerCaseFormType}Number`];
		const currentRowId = currentModification ? currentModification[`${lowerCaseFormType}Number`] : null;

		if(clickedRowId !== currentRowId) {
			setCurrentModification(clickedRow);
			const propertyName = isSubaward ? "subawardNumber" : "awardNumber";
			const updatedRows = formattedRows.map(modification => {
				if(modification[propertyName] === clickedRow[propertyName] && clickedRow.caseInstanceId !== undefined) {
					modification._isSelected = true;
				} else {
					modification._isSelected = false;
				}

				return modification;
			});

			setFormattedRows(updatedRows);

			const isCompletedModification = clickedRow.modificationStatus === MODIFICATION_COMPLETED;
			const isOriginalForm = clickedRow.category?.toLowerCase() === `original ${lowerCaseFormType}`;

			if(isCompletedModification || (isOriginalForm && hasModifications)) {
				setIsModificationChange(true);
			} else {
				setIsModificationChange(false);
			}

			setCurrentModificationNumber(clickedRow.awardId, clickedRow.subawardId, clickedRow.modificationNumber);
		}
	};


	const onPageChange = pageNumber => {
		setPageNumber(pageNumber);
		setUpdateModifications(true);
	};

	const onPageLimitChange = pageLimit => {
		setPageLimit(pageLimit);
		setUpdateModifications(true);
	};

	const shouldDisableModifyButton =
		hasDraft ||
		!isNonEmptyArray(items) ||
		(formStatus !== "Active" && formStatus !== "In Closeout") ||
		!isLatestModification;

	const renderButtons = useCallback(() => {
		const buttons = [];

		const draftButtons = isFormAdmin ? [
			<Button
				key={dictionary.common.buttons.delete || "Delete Modification"}
				onClick={onConfirmDeleteOpen}
				disabled={isFormSaving}
			>
				{dictionary.common.buttons.delete || "Delete Modification"}
			</Button>,
			<Button
				key={dictionary.common.buttons.save || "Set Modification to Active"}
				onClick={onActivateDraftOpen}
				disabled={isFormSaving}
			>
				{dictionary.common.buttons.save || "Set Modification to Active"}
			</Button>
		] : [];

		const modifyButtons = isFormAdmin ? [
			<Button
				key={formSpecificDictionary.buttons.modify || `Modify ${capitalizedFormType}`}
				disabled={shouldDisableModifyButton || isEditMode}
				onClick={onModifyFormOpen}
			>
				{formSpecificDictionary.buttons.modify || `Modify ${capitalizedFormType}`}
			</Button>
		] : [];

		if(hasDraft && isDraftSelected) {
			draftButtons.forEach(button => {
				buttons.push(button);
			});
		} else {
			modifyButtons.forEach(button => {
				buttons.push(button);
			});
		}

		return buttons;
	}, [
		capitalizedFormType,
		dictionary.common.buttons.delete,
		dictionary.common.buttons.save,
		formSpecificDictionary.buttons.modify,
		hasDraft,
		isDraftSelected,
		isFormAdmin,
		onActivateDraftOpen,
		shouldDisableModifyButton,
		isFormSaving,
		isEditMode
	]);

	const renderModificationNumber = modification => {
		if(isNonEmptyObject(modification) && modification.modificationStatus === MODIFICATION_DRAFT) {
			return <em>{modification[`${lowerCaseFormType}Number`] + " (Draft)"}</em>;
		} else if (isNonEmptyObject(modification)) {
			return modification[`${lowerCaseFormType}Number`];
		} else {
			return "";
		}
	};

	useEffect(() => {
		if (componentDidMount && !isLoading && isNonEmptyString(formId)) {
			getFormModifications({ formInfo });

			return () => {
				// Reset modifications when unmounted
				resetModifications();
			};
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ componentDidMount, formId  ]); // Only want to fetch this info on mount

	useEffect(() => {
		if (updateModifications && !isLoading) {
			setUpdateModifications(false);
			refreshFormModifications({ formInfo, paging: { pageLimit, pageOffset } });
		}
	}, [
		isLoading,
		updateModifications
	]);

	return (
		<>
			{isDraftSelected &&
				<div className={styles.draftBanner}>
					<Icon name={"exclamation-triangle"}  />
					<p>This is a draft modification</p>
				</div>
			}
			{isModificationUnderReview &&
				<div className={styles.modificationReviewBanner}>
					<Icon name={"exclamation-triangle"}  />
					<p>Award modification is under review</p>
				</div>
			}
			<ModificationModals
				commentModalData={commentModalData}
				confirmDeleteModalData={confirmDeleteModalData}
				modifyFormModalData={modifyFormModalData}
				activateDraftModalData={activateDraftModalData}
				isSubaward={isSubaward}
				canReroute={canReroute}
			/>
			<div className={styles.modifications} data-component="formModifications">
				<VerticalSpacing>
					<div className={styles.modificationsHeader}>
						<div>
							<h2>
								<CovertButton
									className={showFeedTable ? styles.downArrowButton : styles.upArrowButton}
									onClick={() => setShowFeedTable(!showFeedTable)}
									aria-expanded={showFeedTable}
								>
									<Icon name={"angle-down"}  />
									<div>
										<span>{formSpecificDictionary.currentForm || `Currently Viewing ${capitalizedFormType} form: `}</span>
										<span>{renderModificationNumber(currentModification)}</span>
									</div>
								</CovertButton>
							</h2>
						</div>
						<ButtonGroup>
							{renderButtons()}
							<SetView
								initialColumns={availableColumns}
								columns={activeColumns}
								onChangeColumns={updateActiveColumns}
							/>
						</ButtonGroup>
					</div>
				</VerticalSpacing>
				{ showFeedTable &&
					<FeedTable
						customClass={hasDraft ? styles.feedTable : styles.inDraftFeedTable}
						headers={activeColumns}
						isLoading={isLoading}
						isMultiSelect={false}
						isRefresh={isRefresh}
						isSelectable={true}
						noResultsLabel={dictionary?.common?.feeTable?.noResults || "No Saved Modifications"}
						onPageChange={onPageChange}
						onPageLimitChange={onPageLimitChange}
						onRowClick={onRowClick}
						overflow="visible"
						pageLimit={pageLimit}
						pageOffset={pageOffset}
						rows={formattedRows}
						totalItems={totalItems}
						disabled={isEditMode}
						captionText="Modifications"
					/>
				}

			</div>
		</>
	);
};

Modifications.defaultProps = {
	parentId: ""
};

Modifications.propTypes = {
	caseInstanceId: PropTypes.string,
	formId: PropTypes.string,
	formType: PropTypes.oneOf([ "AWARD", "SUBAWARD", "PROPOSAL" ]).isRequired,
	parentId: PropTypes.string,
	setShouldUpdateForm: PropTypes.func,
};

export default Modifications;
